/**
 * Styled Debug Logger for tracking component and viewer initialization
 * Provides color-coded, formatted console logs for easy debugging
 *
 * ENVIRONMENT-AWARE: Automatically disables in production (except errors)
 * - Development: All logs visible with styled output
 * - Production: Only errors visible for debugging critical issues
 */

import appConfig from '../../appConfig';

// Debug configuration - automatically disabled in production
const DEBUG_CONFIG = {
  enabled: appConfig.debugMode, // true in development, false in production
  categories: {
    COMPONENT: { enabled: true, color: '#2196F3', icon: '📦' },
    VIEWER: { enabled: true, color: '#FF9800', icon: '👁️' },
    POINTCLOUD: { enabled: true, color: '#4CAF50', icon: '☁️' },
    LIFECYCLE: { enabled: true, color: '#607D8B', icon: '♻️' },
    EVENT: { enabled: true, color: '#9C27B0', icon: '⚡' },
    NAVIGATION: { enabled: true, color: '#00BCD4', icon: '🧭' },
    // New categories for broader use cases
    STORE: { enabled: true, color: '#E91E63', icon: '🗄️' },
    API: { enabled: true, color: '#FF5722', icon: '🌐' },
    FORGE: { enabled: true, color: '#795548', icon: '🔧' },
    GENERAL: { enabled: true, color: '#9E9E9E', icon: '📝' },
  }
};

/**
 * Base logger function with styled output (category-based)
 */
function logCategory(category, type, name, data = null) {
  if (!DEBUG_CONFIG.enabled) return;

  const categoryConfig = DEBUG_CONFIG.categories[category];
  if (!categoryConfig || !categoryConfig.enabled) return;

  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  const icon = categoryConfig.icon;
  const color = categoryConfig.color;

  const baseStyle = `
    background: linear-gradient(135deg, ${color} 0%, ${color}dd 100%);
    color: white;
    padding: 4px 8px;
    border-radius: 3px;
    font-weight: bold;
    font-size: 11px;
  `;

  const timeStyle = `
    background: #333;
    color: #fff;
    padding: 4px 6px;
    border-radius: 3px;
    font-size: 10px;
    margin-left: 4px;
  `;

  const nameStyle = `
    background: #f5f5f5;
    color: #333;
    padding: 4px 8px;
    border-radius: 3px;
    font-weight: bold;
    margin-left: 4px;
  `;

  const typeStyle = `
    color: ${color};
    font-weight: bold;
    text-transform: uppercase;
    font-size: 11px;
  `;

  console.log(
    `%c${icon} ${category}%c${timestamp}%c${name}%c ${type}`,
    baseStyle,
    timeStyle,
    nameStyle,
    typeStyle
  );

  if (data) {
    console.log(`%c└─ Data:`, `color: ${color}; font-weight: bold;`, data);
  }
}

// ============================================================================
// GENERIC LOGGING METHODS (for replacing console.log/warn/error throughout codebase)
// ============================================================================

/**
 * Generic debug log - silent in production
 * @param {string} message - Log message
 * @param {any} data - Optional data to log
 */
export const log = (message, data = null) => {
  if (!DEBUG_CONFIG.enabled) return;
  if (data !== null) {
    console.log(`[DEBUG] ${message}`, data);
  } else {
    console.log(`[DEBUG] ${message}`);
  }
};

/**
 * Generic warning log - silent in production
 * @param {string} message - Warning message
 * @param {any} data - Optional data to log
 */
export const warn = (message, data = null) => {
  if (!DEBUG_CONFIG.enabled) return;
  if (data !== null) {
    console.warn(`[WARN] ${message}`, data);
  } else {
    console.warn(`[WARN] ${message}`);
  }
};

/**
 * Generic error log - ALWAYS VISIBLE (even in production)
 * Use for actual errors that need visibility for debugging production issues
 * @param {string} message - Error message
 * @param {any} data - Optional error data
 */
export const error = (message, data = null) => {
  // Errors are always shown for production debugging
  if (data !== null) {
    console.error(`[ERROR] ${message}`, data);
  } else {
    console.error(`[ERROR] ${message}`);
  }
};

/**
 * Generic info log - silent in production
 * @param {string} message - Info message
 * @param {any} data - Optional data to log
 */
export const info = (message, data = null) => {
  if (!DEBUG_CONFIG.enabled) return;
  if (data !== null) {
    console.info(`[INFO] ${message}`, data);
  } else {
    console.info(`[INFO] ${message}`);
  }
};

/**
 * Open a console group - silent in production. MUST be paired with groupEnd().
 *
 * Gated exactly like log/warn/info, so when debug is off both group() and
 * groupEnd() are no-ops and therefore stay balanced (neither runs).
 *
 * @param {string} label - Group label (styled)
 * @param {any} data - Optional data line logged inside the group
 * @param {boolean} collapsed - Use console.groupCollapsed (for sub-groups so the
 *   console isn't overwhelming); leave the top-level group expanded (default)
 */
export const group = (label, data = null, collapsed = false) => {
  if (!DEBUG_CONFIG.enabled) return;
  const style = 'color: #1565C0; font-weight: bold;';
  if (collapsed) {
    console.groupCollapsed(`%c${label}`, style);
  } else {
    console.group(`%c${label}`, style);
  }
  if (data !== null) {
    console.log('%c└─ Data:', style, data);
  }
};

/**
 * Close the most recently opened console group - silent in production.
 * Pairs with group(); gated identically so the pair stays balanced.
 */
export const groupEnd = () => {
  if (!DEBUG_CONFIG.enabled) return;
  console.groupEnd();
};

// ============================================================================
// CATEGORY-BASED LOGGING METHODS (existing API preserved)
// ============================================================================

/**
 * Component lifecycle loggers
 */
export const logComponentCreated = (name, data) => {
  logCategory('COMPONENT', 'CREATED', name, data);
};

export const logComponentMounted = (name, data) => {
  logCategory('COMPONENT', 'MOUNTED', name, data);
};

export const logComponentDestroyed = (name, data) => {
  logCategory('COMPONENT', 'DESTROYED', name, data);
};

/**
 * Viewer initialization loggers
 */
export const logViewerInit = (name, data) => {
  logCategory('VIEWER', 'INITIALIZING', name, data);
};

export const logViewerReady = (name, data) => {
  logCategory('VIEWER', 'READY', name, data);
};

export const logViewerDispose = (name, data) => {
  logCategory('VIEWER', 'DISPOSED', name, data);
};

/**
 * Point cloud loggers
 */
export const logPointCloudLoading = (name, data) => {
  logCategory('POINTCLOUD', 'LOADING', name, data);
};

export const logPointCloudLoaded = (name, data) => {
  logCategory('POINTCLOUD', 'LOADED', name, data);
};

export const logPointCloudError = (name, data) => {
  logCategory('POINTCLOUD', 'ERROR', name, data);
};

/**
 * Event loggers
 */
export const logEvent = (name, data) => {
  logCategory('EVENT', 'FIRED', name, data);
};

/**
 * Navigation loggers
 */
export const logNavigationStart = (name, data) => {
  logCategory('NAVIGATION', 'STARTED', name, data);
};

export const logNavigationComplete = (name, data) => {
  logCategory('NAVIGATION', 'COMPLETED', name, data);
};

export const logNavigationError = (name, data) => {
  logCategory('NAVIGATION', 'ERROR', name, data);
};

export const logNavigationWarning = (name, data) => {
  logCategory('NAVIGATION', 'WARNING', name, data);
};

/**
 * Store loggers (NEW)
 */
export const logStoreAction = (name, data) => {
  logCategory('STORE', 'ACTION', name, data);
};

export const logStoreMutation = (name, data) => {
  logCategory('STORE', 'MUTATION', name, data);
};

export const logStoreError = (name, data) => {
  logCategory('STORE', 'ERROR', name, data);
};

/**
 * API loggers (NEW)
 */
export const logApiRequest = (name, data) => {
  logCategory('API', 'REQUEST', name, data);
};

export const logApiResponse = (name, data) => {
  logCategory('API', 'RESPONSE', name, data);
};

export const logApiError = (name, data) => {
  logCategory('API', 'ERROR', name, data);
};

/**
 * Forge viewer loggers (NEW)
 */
export const logForgeInit = (name, data) => {
  logCategory('FORGE', 'INIT', name, data);
};

export const logForgeAction = (name, data) => {
  logCategory('FORGE', 'ACTION', name, data);
};

export const logForgeError = (name, data) => {
  logCategory('FORGE', 'ERROR', name, data);
};

/**
 * General purpose loggers (NEW)
 */
export const logGeneral = (name, data) => {
  logCategory('GENERAL', 'LOG', name, data);
};

export const logGeneralWarning = (name, data) => {
  logCategory('GENERAL', 'WARNING', name, data);
};

export const logGeneralError = (name, data) => {
  logCategory('GENERAL', 'ERROR', name, data);
};

// ============================================================================
// LIFECYCLE TIMING & CONFIGURATION
// ============================================================================

/**
 * Lifecycle timing tracker
 */
class LifecycleTimer {
  constructor(name) {
    this.name = name;
    this.startTime = performance.now();
  }

  end(message = 'COMPLETED') {
    const duration = (performance.now() - this.startTime).toFixed(2);
    logCategory('LIFECYCLE', message, this.name, { duration: `${duration}ms` });
  }
}

export const startTimer = (name) => {
  return new LifecycleTimer(name);
};

/**
 * Enable/disable debug logging at runtime
 */
export const enableDebug = (enabled = true) => {
  DEBUG_CONFIG.enabled = enabled;
  // This message always shows to confirm the action
  console.log(`%c🐛 Debug logging ${enabled ? 'ENABLED' : 'DISABLED'}`,
    `background: ${enabled ? '#4CAF50' : '#f44336'}; color: white; padding: 8px 16px; font-weight: bold; border-radius: 4px;`);
};

/**
 * Configure category-specific logging
 */
export const configureDebug = (categoryConfig) => {
  Object.assign(DEBUG_CONFIG.categories, categoryConfig);
  if (DEBUG_CONFIG.enabled) {
    console.log('%c🔧 Debug configuration updated',
      'background: #2196F3; color: white; padding: 8px 16px; font-weight: bold; border-radius: 4px;',
      categoryConfig);
  }
};

/**
 * Check if debug mode is enabled
 */
export const isDebugEnabled = () => DEBUG_CONFIG.enabled;

// Export default object with all methods
export default {
  // Generic methods (primary API for replacing console.log)
  log,
  warn,
  error,
  info,
  group,
  groupEnd,
  // Component lifecycle
  logComponentCreated,
  logComponentMounted,
  logComponentDestroyed,
  // Viewer
  logViewerInit,
  logViewerReady,
  logViewerDispose,
  // Point cloud
  logPointCloudLoading,
  logPointCloudLoaded,
  logPointCloudError,
  // Events
  logEvent,
  // Navigation
  logNavigationStart,
  logNavigationComplete,
  logNavigationError,
  logNavigationWarning,
  // Store (NEW)
  logStoreAction,
  logStoreMutation,
  logStoreError,
  // API (NEW)
  logApiRequest,
  logApiResponse,
  logApiError,
  // Forge (NEW)
  logForgeInit,
  logForgeAction,
  logForgeError,
  // General (NEW)
  logGeneral,
  logGeneralWarning,
  logGeneralError,
  // Utilities
  startTimer,
  enableDebug,
  configureDebug,
  isDebugEnabled,
};
